import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, TrendingUp, LineChart as LineChartIcon, 
  Loader, AlertCircle, ArrowUpRight, ArrowDownRight,
  Calendar, Info
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { format, parseISO, subMonths, subYears, isAfter } from 'date-fns';

// Type definitions
interface Observation {
  date: string;
  value: string;
}

interface TreasuryData {
  series: Array<{
    id: string;
    seriesId: string;
    name: string;
    unit: string;
    source: string;
    frequency: string;
    latestValue: string | number;
    change: number;
    changePercent: number;
    yearChange?: number;
    yearChangePercent?: number;
    observations: Observation[];
  }>;
}

interface ProcessedDataPoint {
  date: string;
  value: number;
  formattedDate: string;
  shortDate: string;
  displayValue?: string;
}

type TimeframeType = '1M' | '3M' | '6M' | '1Y' | '5Y' | 'ALL';
type TabType = 'fiscal' | 'inflation' | 'markets';

// Helper functions
const formatNumberWithCommas = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatLargeNumber = (value: number, decimals: number = 2): string => {
  if (Math.abs(value) >= 1e12) {
    return `$${(value / 1e12).toFixed(decimals)} trillion`;
  } else if (Math.abs(value) >= 1e9) {
    return `$${(value / 1e9).toFixed(decimals)} billion`;
  } else if (Math.abs(value) >= 1e6) {
    return `$${(value / 1e6).toFixed(decimals)} million`;
  }
  return `$${formatNumberWithCommas(value)}`;
};

const formatCompactNumber = (value: number): string => {
  if (Math.abs(value) >= 1e12) {
    return `${(value / 1e12).toFixed(1)}T`;
  } else if (Math.abs(value) >= 1e9) {
    return `${(value / 1e9).toFixed(1)}B`;
  } else if (Math.abs(value) >= 1e6) {
    return `${(value / 1e6).toFixed(0)}M`;
  } else if (Math.abs(value) >= 1e3) {
    return `${(value / 1e3).toFixed(0)}K`;
  }
  return value.toFixed(0);
};

const formatPercent = (value: number): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const parseValue = (value: string | number): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value) || 0;
  return 0;
};

const TradFiPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [treasuryData, setTreasuryData] = useState<TreasuryData | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeType>('1Y');
  const [activeTab, setActiveTab] = useState<TabType>('fiscal');
  const [inflationData, setInflationData] = useState<any>(null);
  const [fedFundsData, setFedFundsData] = useState<any>(null);
  const [yieldCurveData, setYieldCurveData] = useState<any>(null);
  const [inflationChartType, setInflationChartType] = useState<'rate' | 'index'>('rate');
  
  // Sync activeTab with URL parameter
  useEffect(() => {
    if (tab && ['fiscal', 'inflation', 'markets'].includes(tab)) {
      setActiveTab(tab as TabType);
    } else if (!tab) {
      // Default to fiscal if no tab specified
      setActiveTab('fiscal');
    } else {
      // Invalid tab, redirect to fiscal
      navigate('/tradfi/fiscal', { replace: true });
    }
  }, [tab, navigate]);

  useEffect(() => {
    fetchTreasuryData();
  }, []);

  useEffect(() => {
    if (activeTab === 'inflation') {
      fetchInflationData();
    } else if (activeTab === 'markets') {
      fetchMarketsData();
    }
  }, [activeTab]);

  const fetchTreasuryData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const fullUrl = `${apiUrl}/api/economic/fiscal`;
      console.log('Fetching from:', fullUrl, 'API URL:', apiUrl);
      
      const response = await fetch(fullUrl);
      console.log('Response status:', response.status, 'OK:', response.ok);
      console.log('Response headers:', response.headers.get('content-type'));
      
      if (!response.ok) throw new Error('Failed to fetch Treasury data');
      
      const data = await response.json();
      setTreasuryData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching Treasury data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setLoading(false);
    }
  };

  const fetchInflationData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/economic/dashboard`);
      if (response.ok) {
        const data = await response.json();
        setInflationData(data.inflation);
      }
    } catch (err) {
      console.error('Error fetching inflation data:', err);
    }
  };

  const fetchMarketsData = async () => {
    try {
      const [dashboardResponse, yieldResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/economic/dashboard`),
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/yield-curve/history`)
      ]);
      
      if (dashboardResponse.ok) {
        const data = await dashboardResponse.json();
        setFedFundsData(data.monetary?.fedFunds);
      }
      
      if (yieldResponse.ok) {
        const yieldData = await yieldResponse.json();
        setYieldCurveData(yieldData);
      }
    } catch (err) {
      console.error('Error fetching markets data:', err);
    }
  };

  // Filter data based on selected timeframe
  const getFilteredData = useCallback((observations: Observation[]) => {
    if (!observations || observations.length === 0) return [];
    
    const now = new Date();
    let cutoffDate: Date;

    switch (selectedTimeframe) {
      case '1M':
        cutoffDate = subMonths(now, 1);
        break;
      case '3M':
        cutoffDate = subMonths(now, 3);
        break;
      case '6M':
        cutoffDate = subMonths(now, 6);
        break;
      case '1Y':
        cutoffDate = subYears(now, 1);
        break;
      case '5Y':
        cutoffDate = subYears(now, 5);
        break;
      case 'ALL':
      default:
        return observations;
    }

    return observations.filter(obs => {
      const obsDate = parseISO(obs.date);
      return isAfter(obsDate, cutoffDate);
    });
  }, [selectedTimeframe]);

  // Process data for charts
  const processDataForChart = useCallback((observations: Observation[]): ProcessedDataPoint[] => {
    // Sort observations by date (oldest to newest) first
    const sortedObs = [...observations].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    const filtered = getFilteredData(sortedObs);
    
    
    return filtered.map(obs => {
      const value = parseValue(obs.value);
      const date = parseISO(obs.date);
      
      return {
        date: obs.date,
        value,
        formattedDate: format(date, 'MMM d, yyyy'),
        shortDate: format(date, 'MMM yyyy'),
        displayValue: formatLargeNumber(value)
      };
    });
  }, [getFilteredData]);

  // Render metric card
  const renderMetricCard = (series: any) => {
    const latestValue = parseValue(series.latestValue);
    const change = series.change || 0;
    const changePercent = series.changePercent || 0;
    const yearChange = series.yearChange || 0;
    const yearChangePercent = series.yearChangePercent || 0;
    const isDebt = series.name.toLowerCase().includes('debt');
    
    const latestDate = series.observations?.[series.observations.length - 1]?.date;
    const formattedDate = latestDate ? format(parseISO(latestDate), 'MMM d, yyyy') : '';

    // Mini chart data - last 30 observations
    const miniChartData = series.observations?.slice(-30).map((obs: Observation) => ({
      value: parseValue(obs.value)
    })) || [];

    return (
      <motion.div
        key={series.id}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        className="bg-gradient-to-br from-zinc-900/90 to-zinc-800/50 backdrop-blur-md border border-zinc-700/50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-sm font-medium text-zinc-400">{series.name}</h3>
            <p className="text-xs text-zinc-500 mt-1">{series.unit}</p>
          </div>
          <span className="text-xs text-zinc-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formattedDate}
          </span>
        </div>

        <div className="space-y-3">
          <div>
            <span className="text-3xl font-bold text-white">
              {formatLargeNumber(latestValue, series.unit === 'Percent' ? 2 : 0)}
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className={`flex items-center gap-1 ${
              isDebt ? (change > 0 ? 'text-red-400' : 'text-green-400') : 
              (change >= 0 ? 'text-emerald-400' : 'text-rose-400')
            }`}>
              {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span className="font-medium">{formatPercent(changePercent)}</span>
              <span className="text-xs text-zinc-500">MoM</span>
            </div>

            {yearChangePercent !== null && (
              <div className={`flex items-center gap-1 ${
                isDebt ? (yearChange >= 0 ? 'text-red-400' : 'text-green-400') :
                (yearChange >= 0 ? 'text-emerald-400' : 'text-rose-400')
              }`}>
                {yearChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span className="font-medium">{formatPercent(yearChangePercent)}</span>
                <span className="text-xs text-zinc-500">YoY</span>
              </div>
            )}
          </div>
        </div>

        {/* Mini trend chart */}
        <div className="mt-4 h-12">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={miniChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`gradient-${series.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={change >= 0 ? '#10B981' : '#EF4444'} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={change >= 0 ? '#10B981' : '#EF4444'} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke={change >= 0 ? '#10B981' : '#EF4444'} 
                strokeWidth={1.5}
                fill={`url(#gradient-${series.id})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
    );
  };

  // Render debt chart
  const renderDebtChart = (series: any) => {
    const processedData = processDataForChart(series.observations);

    // Calculate daily changes
    const dataWithChanges = processedData.map((point, index) => ({
      ...point,
      dailyChange: index > 0 ? point.value - processedData[index - 1].value : 0,
    }));

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-zinc-900/90 to-zinc-800/50 backdrop-blur-md border border-zinc-700/50 rounded-2xl p-6 shadow-lg"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-white">{series.name}</h3>
            <p className="text-sm text-zinc-400 mt-1">
              {series.unit} • Updated {format(new Date(), 'MMM d, yyyy')}
            </p>
          </div>
          <Info className="w-5 h-5 text-zinc-500" />
        </div>

        {/* Main debt trend chart */}
        <div className="h-64 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={processedData} margin={{ top: 10, right: 10, left: 60, bottom: 30 }}>
              <defs>
                <linearGradient id="debtGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" opacity={0.3} />
              <XAxis 
                dataKey="shortDate" 
                stroke="#71717A"
                tick={{ fontSize: 11, fill: '#71717A' }}
                axisLine={{ stroke: '#27272A' }}
              />
              <YAxis
                stroke="#71717A"
                tick={{ fontSize: 11, fill: '#71717A' }}
                tickFormatter={(value) => formatCompactNumber(value)}
                axisLine={{ stroke: '#27272A' }}
                domain={['dataMin - 500000000000', 'dataMax + 500000000000']}
                tickCount={8}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(24, 24, 27, 0.95)',
                  border: '1px solid rgba(63, 63, 70, 0.5)',
                  borderRadius: '12px',
                  color: '#FFFFFF',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
                }}
                labelStyle={{ color: '#A1A1AA', fontSize: '12px', marginBottom: '4px' }}
                itemStyle={{ color: '#FFFFFF', fontSize: '14px' }}
                formatter={(value: number) => [formatLargeNumber(value), 'Total Debt']}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#8B5CF6"
                strokeWidth={2}
                fill="url(#debtGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Daily changes bar chart */}
        <div>
          <h4 className="text-sm font-medium text-zinc-300 mb-3">Daily Changes</h4>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dataWithChanges.slice(-30)} margin={{ top: 10, right: 10, left: 60, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" opacity={0.3} />
                <XAxis 
                  dataKey="shortDate" 
                  stroke="#71717A"
                  tick={false}
                  axisLine={{ stroke: '#27272A' }}
                />
                <YAxis
                  stroke="#71717A"
                  tick={{ fontSize: 10, fill: '#71717A' }}
                  tickFormatter={(value) => formatCompactNumber(value)}
                  axisLine={{ stroke: '#27272A' }}
                  domain={['dataMin - dataMin * 0.1', 'dataMax + dataMax * 0.1']}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(24, 24, 27, 0.95)',
                    border: '1px solid rgba(63, 63, 70, 0.5)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    backdropFilter: 'blur(12px)'
                  }}
                  labelStyle={{ color: '#A1A1AA', fontSize: '12px', marginBottom: '4px' }}
                  itemStyle={{ color: '#FFFFFF', fontSize: '14px' }}
                  formatter={(value: number) => [
                    formatLargeNumber(value),
                    value >= 0 ? 'Increase' : 'Decrease'
                  ]}
                />
                <Bar dataKey="dailyChange" radius={[2, 2, 0, 0]}>
                  {dataWithChanges.slice(-30).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.dailyChange >= 0 ? '#DC2626' : '#10B981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>
    );
  };

  // Render deficit/surplus chart
  const renderDeficitChart = (series: any) => {
    const processedData = processDataForChart(series.observations);

    // Get max absolute value for symmetric Y-axis
    const maxAbsValue = Math.max(...processedData.map(d => Math.abs(d.value)));
    const yDomain = [-maxAbsValue * 1.1, maxAbsValue * 1.1];

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="bg-gradient-to-br from-zinc-900/90 to-zinc-800/50 backdrop-blur-md border border-zinc-700/50 rounded-2xl p-6 shadow-lg"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-white">{series.name}</h3>
            <p className="text-sm text-zinc-400 mt-1">
              {series.unit} • Green = Surplus, Red = Deficit
            </p>
          </div>
          <Info className="w-5 h-5 text-zinc-500" />
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={processedData} margin={{ top: 10, right: 10, left: 60, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" opacity={0.3} />
              <XAxis 
                dataKey="shortDate" 
                stroke="#71717A"
                tick={{ fontSize: 11, fill: '#71717A' }}
                axisLine={{ stroke: '#27272A' }}
              />
              <YAxis
                stroke="#71717A"
                tick={{ fontSize: 11, fill: '#71717A' }}
                tickFormatter={(value) => formatCompactNumber(value)}
                domain={yDomain}
                axisLine={{ stroke: '#27272A' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(24, 24, 27, 0.95)',
                  border: '1px solid rgba(63, 63, 70, 0.5)',
                  borderRadius: '12px',
                  color: '#FFFFFF',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
                }}
                labelStyle={{ color: '#A1A1AA', fontSize: '12px', marginBottom: '4px' }}
                itemStyle={{ color: '#FFFFFF', fontSize: '14px' }}
                formatter={(value: number) => {
                  const label = value > 0 ? 'Surplus' : 'Deficit';
                  const color = value > 0 ? '#10B981' : '#EF4444';
                  return [
                    <span style={{ color }}>{formatLargeNumber(Math.abs(value))}</span>,
                    label
                  ];
                }}
              />
              <ReferenceLine y={0} stroke="#71717A" strokeDasharray="3 3" />
              <Bar
                dataKey="value"
                radius={[4, 4, 0, 0]}
              >
                {processedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.value >= 0 ? '#10B981' : '#DC2626'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="text-center">
            <p className="text-xs text-zinc-500 mb-1">Latest</p>
            <p className={`text-lg font-semibold ${processedData[processedData.length - 1]?.value >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {formatLargeNumber(Math.abs(processedData[processedData.length - 1]?.value || 0))}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-zinc-500 mb-1">Average ({selectedTimeframe})</p>
            <p className="text-lg font-semibold text-zinc-300">
              {formatLargeNumber(Math.abs(processedData.reduce((sum, d) => sum + d.value, 0) / processedData.length || 0))}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-zinc-500 mb-1">Trend</p>
            <p className={`text-lg font-semibold ${series.change >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {series.change >= 0 ? 'Improving' : 'Worsening'}
            </p>
          </div>
        </div>
      </motion.div>
    );
  };

  // Render inflation tab
  const renderInflationTab = () => {
    if (!inflationData) {
      return <div className="text-center py-8 text-zinc-400">Loading inflation data...</div>;
    }

    // Check if we have the required data
    if (!inflationData.cpi || !inflationData.cpi.observations || inflationData.cpi.observations.length === 0) {
      return <div className="text-center py-8 text-zinc-400">No CPI data available</div>;
    }

    // Get filtered data based on timeframe for inflation visualization
    const getFilteredInflationData = (observations: any[]) => {
      if (!observations || observations.length === 0) return [];
      
      // First sort the data by date (oldest to newest)
      const sortedData = [...observations].sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      // Use the same filtering as other charts for consistency
      if (inflationChartType === 'index') {
        // For index view, use the global filter which respects actual date ranges
        return getFilteredData(sortedData);
      }
      
      // For inflation rate calculations, we need extra data for YoY calculations
      let dataMonths: number;

      switch (selectedTimeframe) {
        case '1M':
          dataMonths = 14; // Need 13 months for YoY calculation
          break;
        case '3M':
          dataMonths = 16;
          break;
        case '6M':
          dataMonths = 19;
          break;
        case '1Y':
          dataMonths = 25;
          break;
        case '5Y':
          dataMonths = 73;
          break;
        case 'ALL':
        default:
          return sortedData; // Return ALL data sorted
      }

      // Get last N months of data
      return sortedData.slice(-dataMonths);
    };

    // Process chart data outside of JSX
    const chartData = (() => {
      const allCpiData = inflationData.cpi.observations || [];
      const allCoreCpiData = inflationData.coreCpi.observations || [];
      
      const filteredData = getFilteredInflationData(allCpiData);
      const filteredCoreCpiData = getFilteredInflationData(allCoreCpiData);
      
      if (inflationChartType === 'index') {
        return filteredData.map((obs: any) => {
          const coreCpiObs = filteredCoreCpiData.find((c: any) => c.date === obs.date);
          return {
            date: format(parseISO(obs.date), 'MMM yy'),
            cpi: Number(parseValue(obs.value)),
            coreCpi: coreCpiObs ? Number(parseValue(coreCpiObs.value)) : Number(parseValue(obs.value)) // Use CPI value as fallback
          };
        }).filter(item => item.cpi > 0); // Filter out any zero or negative values
      } else {
        // Calculate inflation rates
        const inflationRateData = filteredData.map((obs: any) => {
          const currentDate = parseISO(obs.date);
          const yearAgoDate = new Date(currentDate);
          yearAgoDate.setFullYear(yearAgoDate.getFullYear() - 1);
          
          const yearAgoObs = allCpiData.find((prevObs: any) => {
            const prevDate = parseISO(prevObs.date);
            const diffMonths = Math.abs((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
            return diffMonths >= 11 && diffMonths <= 13;
          });
          
          const coreCpiObs = allCoreCpiData.find((c: any) => c.date === obs.date);
          const yearAgoCoreCpiObs = coreCpiObs ? allCoreCpiData.find((prevObs: any) => {
            const prevDate = parseISO(prevObs.date);
            const diffMonths = Math.abs((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
            return diffMonths >= 11 && diffMonths <= 13;
          }) : null;
          
          let cpiInflation = null;
          let coreCpiInflation = null;
          
          if (yearAgoObs) {
            const currentCpi = parseValue(obs.value);
            const yearAgoCpi = parseValue(yearAgoObs.value);
            cpiInflation = ((currentCpi - yearAgoCpi) / yearAgoCpi) * 100;
          }
          
          if (coreCpiObs && yearAgoCoreCpiObs) {
            const currentCoreCpi = parseValue(coreCpiObs.value);
            const yearAgoCoreCpi = parseValue(yearAgoCoreCpiObs.value);
            coreCpiInflation = ((currentCoreCpi - yearAgoCoreCpi) / yearAgoCoreCpi) * 100;
          }
          
          return {
            date: format(parseISO(obs.date), 'MMM yy'),
            cpiInflation: cpiInflation ? Number(cpiInflation) : null,
            coreCpiInflation: coreCpiInflation ? Number(coreCpiInflation) : null
          };
        }).filter(item => item.cpiInflation !== null);
        
        return inflationRateData;
      }
    })();

    // Debug log to check chart data
    console.log('Chart data:', chartData);
    console.log('Chart data length:', chartData.length);
    console.log('Inflation chart type:', inflationChartType);
    if (chartData.length > 0) {
      console.log('First chart item:', chartData[0]);
      console.log('Last chart item:', chartData[chartData.length - 1]);
      console.log('LineChart component available:', typeof LineChart);
      console.log('ResponsiveContainer available:', typeof ResponsiveContainer);
    }

    const cpiValue = parseValue(inflationData.cpi.latestValue);
    const coreCpiValue = parseValue(inflationData.coreCpi.latestValue);
    const m2Value = parseValue(inflationData.m2.latestValue);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6"
          >
            <h3 className="text-sm font-medium text-zinc-400 mb-2">CPI (All Items)</h3>
            <p className="text-3xl font-bold text-white">{cpiValue.toFixed(1)}</p>
            <p className={`text-sm mt-1 ${inflationData.cpi.yearChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {formatPercent(inflationData.cpi.yearChangePercent || 0)} YoY
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6"
          >
            <h3 className="text-sm font-medium text-zinc-400 mb-2">Core CPI</h3>
            <p className="text-3xl font-bold text-white">{coreCpiValue.toFixed(1)}</p>
            <p className={`text-sm mt-1 ${inflationData.coreCpi.yearChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {formatPercent(inflationData.coreCpi.yearChangePercent || 0)} YoY
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6"
          >
            <h3 className="text-sm font-medium text-zinc-400 mb-2">M2 Money Supply</h3>
            <p className="text-3xl font-bold text-white">{formatCompactNumber(m2Value * 1e9)}</p>
            <p className={`text-sm mt-1 ${inflationData.m2.yearChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {formatPercent(inflationData.m2.yearChangePercent || 0)} YoY
            </p>
          </motion.div>
        </div>

        {/* Main CPI Analysis Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">CPI Analysis</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setInflationChartType('rate')}
                className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                  inflationChartType === 'rate'
                    ? 'bg-violet-500 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                Inflation Rate
              </button>
              <button
                onClick={() => setInflationChartType('index')}
                className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                  inflationChartType === 'index'
                    ? 'bg-violet-500 text-white'
                    : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                }`}
              >
                CPI Index (Log)
              </button>
            </div>
          </div>
          
          {/* Custom Bar Chart Visualization */}
          <div className="bg-zinc-800/30 rounded-lg p-4">
            {chartData.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-zinc-300">
                    {inflationChartType === 'rate' ? 'Inflation Rate (%)' : 'CPI Index Values'}
                  </h4>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-emerald-400 rounded-full"></div>
                      <span className="text-zinc-400">
                        {inflationChartType === 'rate' ? 'CPI' : 'CPI Index'}
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-violet-400 rounded-full"></div>
                      <span className="text-zinc-400">
                        {inflationChartType === 'rate' ? 'Core CPI' : 'Core CPI Index'}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Custom Bar Chart */}
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {(() => {
                    // Show more data points for longer timeframes
                    let displayCount = 12;
                    switch (selectedTimeframe) {
                      case '1M': displayCount = 2; break;
                      case '3M': displayCount = 4; break;
                      case '6M': displayCount = 8; break;
                      case '1Y': displayCount = 12; break;
                      case '5Y': displayCount = 24; break;
                      case 'ALL': displayCount = Math.min(60, chartData.length); break;
                    }
                    return chartData.slice(-displayCount);
                  })().map((item: any, index: number) => {
                    const cpiValue = inflationChartType === 'rate' 
                      ? item.cpiInflation 
                      : item.cpi;
                    const coreCpiValue = inflationChartType === 'rate' 
                      ? item.coreCpiInflation 
                      : item.coreCpi;
                    
                    const maxValue = inflationChartType === 'rate' ? 8 : 350; // Adjust max scale
                    const cpiWidth = Math.max(5, (Math.abs(cpiValue) / maxValue) * 100);
                    const coreCpiWidth = Math.max(5, (Math.abs(coreCpiValue || cpiValue) / maxValue) * 100);

                    return (
                      <div key={item.date} className="grid grid-cols-12 gap-2 items-center text-xs">
                        <div className="col-span-2 text-zinc-400 font-mono">
                          {item.date}
                        </div>
                        
                        {/* CPI Bar */}
                        <div className="col-span-5">
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-zinc-700 rounded-full h-4 relative overflow-hidden">
                              <div 
                                className="h-full bg-emerald-400 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min(100, cpiWidth)}%` }}
                              />
                              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-white">
                                {inflationChartType === 'rate' 
                                  ? `${cpiValue?.toFixed(1)}%` 
                                  : cpiValue?.toFixed(0)
                                }
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Core CPI Bar */}
                        <div className="col-span-5">
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-zinc-700 rounded-full h-4 relative overflow-hidden">
                              <div 
                                className="h-full bg-violet-400 rounded-full transition-all duration-300"
                                style={{ width: `${Math.min(100, coreCpiWidth)}%` }}
                              />
                              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-white">
                                {inflationChartType === 'rate' 
                                  ? `${coreCpiValue?.toFixed(1)}%` 
                                  : coreCpiValue?.toFixed(0)
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary Stats */}
                <div className="border-t border-zinc-700 pt-3 grid grid-cols-3 gap-4 text-xs">
                  <div className="text-center">
                    <p className="text-zinc-400 mb-1">Latest</p>
                    <p className="text-emerald-400 font-medium">
                      {inflationChartType === 'rate' 
                        ? `${(chartData[chartData.length - 1] as any)?.cpiInflation?.toFixed(2)}%`
                        : (chartData[chartData.length - 1] as any)?.cpi?.toFixed(0)
                      }
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-zinc-400 mb-1">Average</p>
                    <p className="text-white font-medium">
                      {inflationChartType === 'rate' 
                        ? `${((chartData as any[]).reduce((sum: number, d: any) => sum + (d.cpiInflation || 0), 0) / chartData.length).toFixed(2)}%`
                        : ((chartData as any[]).reduce((sum: number, d: any) => sum + (d.cpi || 0), 0) / chartData.length).toFixed(0)
                      }
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-zinc-400 mb-1">Trend</p>
                    <p className="text-violet-400 font-medium">
                      {(() => {
                        const recent = (chartData as any[]).slice(-3);
                        if (inflationChartType === 'rate') {
                          const trend = (recent[2]?.cpiInflation || 0) - (recent[0]?.cpiInflation || 0);
                          return trend > 0.1 ? '↗️ Rising' : trend < -0.1 ? '↘️ Falling' : '→ Stable';
                        } else {
                          const trend = (recent[2]?.cpi || 0) - (recent[0]?.cpi || 0);
                          return trend > 1 ? '↗️ Rising' : trend < -1 ? '↘️ Falling' : '→ Stable';
                        }
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-zinc-400">
                No data available for visualization
              </div>
            )}
          </div>

          {/* Data Summary */}
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div className="bg-zinc-800/30 rounded-lg p-3">
              <p className="text-zinc-400 text-xs mb-1">Latest CPI Reading</p>
              <p className="text-white font-semibold">
                {inflationChartType === 'rate' && chartData.length > 0 
                  ? `${(chartData[chartData.length - 1] as any).cpiInflation?.toFixed(2) || 'N/A'}%`
                  : cpiValue.toFixed(1)
                }
              </p>
            </div>
            <div className="bg-zinc-800/30 rounded-lg p-3">
              <p className="text-zinc-400 text-xs mb-1">Latest Core CPI</p>
              <p className="text-white font-semibold">
                {inflationChartType === 'rate' && chartData.length > 0
                  ? `${(chartData[chartData.length - 1] as any).coreCpiInflation?.toFixed(2) || 'N/A'}%`
                  : coreCpiValue.toFixed(1)
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* M2 Money Supply Chart */}
        {inflationData?.m2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gradient-to-br from-zinc-900/90 to-zinc-800/50 backdrop-blur-md border border-zinc-700/50 rounded-2xl p-6 shadow-lg"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-white">M2 Money Supply</h3>
                <p className="text-sm text-zinc-400 mt-1">
                  Billions of USD • Updated {format(new Date(), 'MMM d, yyyy')}
                </p>
              </div>
              <Info className="w-5 h-5 text-zinc-500" />
            </div>
          
            {/* Main M2 trend chart */}
            <div className="h-64 mb-6">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={processDataForChart(inflationData.m2.observations)} margin={{ top: 10, right: 10, left: 60, bottom: 30 }}>
                  <defs>
                    <linearGradient id="m2Gradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" opacity={0.3} />
                  <XAxis 
                    dataKey="shortDate" 
                    stroke="#71717A"
                    tick={{ fontSize: 11, fill: '#71717A' }}
                    axisLine={{ stroke: '#27272A' }}
                  />
                  <YAxis
                    stroke="#71717A"
                    tick={{ fontSize: 11, fill: '#71717A' }}
                    tickFormatter={(value) => formatCompactNumber(value * 1e9)}
                    axisLine={{ stroke: '#27272A' }}
                    domain={['dataMin - 500', 'dataMax + 500']}
                    tickCount={8}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(24, 24, 27, 0.95)',
                      border: '1px solid rgba(63, 63, 70, 0.5)',
                      borderRadius: '12px',
                      color: '#FFFFFF',
                      backdropFilter: 'blur(12px)',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
                    }}
                    labelStyle={{ color: '#A1A1AA', fontSize: '12px', marginBottom: '4px' }}
                    itemStyle={{ color: '#FFFFFF', fontSize: '14px' }}
                    formatter={(value: number) => [formatLargeNumber(value * 1e9), 'M2 Money Supply']}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fill="url(#m2Gradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Growth rate chart */}
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-3">Year-over-Year Growth Rate</h4>
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={(() => {
                      // Use ALL M2 data for YoY calculations, not just filtered data
                      const allM2Data = inflationData.m2.observations || [];
                      const sortedAllData = [...allM2Data].sort((a, b) => 
                        new Date(a.date).getTime() - new Date(b.date).getTime()
                      );
                      
                      // Process all data for YoY calculations
                      const allProcessedData = sortedAllData.map(obs => ({
                        date: obs.date,
                        value: Number(parseValue(obs.value)),
                        formattedDate: format(parseISO(obs.date), 'MMM d, yyyy'),
                        shortDate: format(parseISO(obs.date), 'MMM yyyy')
                      }));
                      
                      // Calculate YoY for all data points
                      const yoyData = allProcessedData.map((point, index) => {
                        const currentDate = parseISO(point.date);
                        const yearAgoTarget = new Date(currentDate);
                        yearAgoTarget.setFullYear(yearAgoTarget.getFullYear() - 1);
                        
                        const yearAgoPoint = allProcessedData.find((p, i) => {
                          const pDate = parseISO(p.date);
                          const diffMonths = Math.abs((currentDate.getTime() - pDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
                          return diffMonths >= 11 && diffMonths <= 13 && i < index;
                        });
                        
                        let yoyGrowth = null;
                        if (yearAgoPoint && yearAgoPoint.value > 0) {
                          yoyGrowth = ((point.value - yearAgoPoint.value) / yearAgoPoint.value) * 100;
                        }
                        
                        return {
                          ...point,
                          yoyGrowth
                        };
                      }).filter(d => d.yoyGrowth !== null);
                      
                      // Now filter the YoY data based on selected timeframe
                      // Convert back to the format expected by getFilteredData
                      const yoyObservations = yoyData.map(d => ({
                        date: d.date,
                        value: d.yoyGrowth?.toString() || '0'
                      }));
                      
                      const filteredYoyObs = getFilteredData(yoyObservations);
                      
                      // Convert back to chart format
                      return filteredYoyObs.map(obs => {
                        const originalData = yoyData.find(d => d.date === obs.date);
                        return originalData || { 
                          date: obs.date, 
                          value: 0, 
                          formattedDate: '', 
                          shortDate: '', 
                          yoyGrowth: Number(obs.value) 
                        };
                      });
                    })()}
                    margin={{ top: 10, right: 10, left: 40, bottom: 20 }}
                  >
                    <defs>
                      <linearGradient id="m2GrowthGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" opacity={0.3} />
                    <XAxis 
                      dataKey="shortDate" 
                      stroke="#71717A"
                      tick={false}
                      axisLine={{ stroke: '#27272A' }}
                    />
                    <YAxis
                      stroke="#71717A"
                      tick={{ fontSize: 10, fill: '#71717A' }}
                      tickFormatter={(value) => `${value.toFixed(0)}%`}
                      axisLine={{ stroke: '#27272A' }}
                      domain={['dataMin - 2', 'dataMax + 2']}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(24, 24, 27, 0.95)',
                        border: '1px solid rgba(63, 63, 70, 0.5)',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        backdropFilter: 'blur(12px)'
                      }}
                      labelStyle={{ color: '#A1A1AA', fontSize: '12px', marginBottom: '4px' }}
                      itemStyle={{ color: '#FFFFFF', fontSize: '14px' }}
                      formatter={(value: number) => [
                        `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`,
                        'YoY Growth'
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="yoyGrowth"
                      stroke="#10B981"
                      strokeWidth={1.5}
                      fill="url(#m2GrowthGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="text-center">
                <p className="text-xs text-zinc-500 mb-1">Current Level</p>
                <p className="text-lg font-semibold text-blue-400">
                  {formatLargeNumber(parseValue(inflationData.m2.latestValue) * 1e9)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-zinc-500 mb-1">YoY Change</p>
                <p className={`text-lg font-semibold ${inflationData.m2.yearChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {formatPercent(inflationData.m2.yearChangePercent || 0)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-zinc-500 mb-1">Since 2020</p>
                <p className="text-lg font-semibold text-violet-400">
                  {(() => {
                    const current = parseValue(inflationData.m2.latestValue);
                    // Approximate M2 in early 2020 was around $15.3T
                    const jan2020Approx = 15300;
                    const growth = ((current - jan2020Approx) / jan2020Approx) * 100;
                    return `+${growth.toFixed(0)}%`;
                  })()}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800 rounded-xl p-6 mt-6"
        >
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Info className="w-5 h-5 text-violet-400" />
            Understanding Inflation Data
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-zinc-300">
            <div>
              <h5 className="font-medium text-white mb-2">{inflationChartType === 'rate' ? 'Inflation Rate (CPI)' : 'CPI Index (Logarithmic)'}</h5>
              <p>{inflationChartType === 'rate' ? 
                'Year-over-year percentage change in Consumer Price Index. Shows actual purchasing power erosion - e.g., 3% means goods cost 3% more than last year.' :
                'Raw CPI index values on logarithmic scale. Compresses exponential growth to show relative changes more clearly over long time periods.'
              }</p>
            </div>
            <div>
              <h5 className="font-medium text-white mb-2">{inflationChartType === 'rate' ? 'Core Inflation Rate' : 'Core CPI Index'}</h5>
              <p>{inflationChartType === 'rate' ? 
                'YoY change in Core CPI, excluding volatile food and energy. Provides clearer view of underlying inflation trends for Fed policy decisions.' :
                'Core CPI excludes volatile food and energy prices. Log scale reveals steady long-term price level trends without volatile fluctuations.'
              }</p>
            </div>
            <div>
              <h5 className="font-medium text-white mb-2">Historical Context</h5>
              <p>{inflationChartType === 'rate' ? 
                '2% is Fed target. 1970s saw double-digit inflation, 2010s had subdued inflation near 0%. Current levels show recent inflationary pressures.' :
                'CPI baseline is 1982-84=100. Log scale shows exponential price growth is normal - steep increases indicate periods of high inflation like the 1970s.'
              }</p>
            </div>
            <div>
              <h5 className="font-medium text-white mb-2">{inflationChartType === 'rate' ? 'Economic Impact' : 'Chart Benefits'}</h5>
              <p>{inflationChartType === 'rate' ? 
                'High inflation erodes purchasing power, low inflation may signal weak demand. Fed adjusts interest rates to maintain ~2% target inflation.' :
                'Logarithmic scale makes it easier to compare relative price changes across decades. Equal slopes represent equal percentage growth rates.'
              }</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // Render markets tab
  const renderMarketsTab = () => {
    // Get filtered data based on timeframe
    const getFilteredMarketsData = (observations: any[]) => {
      if (!observations || observations.length === 0) return [];
      
      // First sort the data by date (oldest to newest)
      const sortedData = [...observations].sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      const now = new Date();
      let cutoffDate: Date;

      switch (selectedTimeframe) {
        case '1M':
          cutoffDate = subMonths(now, 1);
          break;
        case '3M':
          cutoffDate = subMonths(now, 3);
          break;
        case '6M':
          cutoffDate = subMonths(now, 6);
          break;
        case '1Y':
          cutoffDate = subYears(now, 1);
          break;
        case '5Y':
          cutoffDate = subYears(now, 5);
          break;
        case 'ALL':
        default:
          return sortedData; // Return ALL data sorted
      }

      return sortedData.filter((obs: any) => {
        const obsDate = parseISO(obs.date);
        return isAfter(obsDate, cutoffDate);
      });
    };

    return (
      <div className="space-y-6">
        {fedFundsData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Federal Funds Rate</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={(() => {
                    const filteredData = getFilteredMarketsData(fedFundsData.observations);
                    const dataPoints = filteredData.length;
                    
                    // Adjust date format based on data density - daily data needs special handling
                    const dateFormat = dataPoints > 2000 ? 'yyyy' : dataPoints > 500 ? 'MMM yy' : 'MMM dd, yy';
                    
                    return filteredData.map((obs: any) => ({
                      date: format(parseISO(obs.date), dateFormat),
                      fullDate: format(parseISO(obs.date), 'MMM dd, yyyy'),
                      rate: parseValue(obs.value)
                    }));
                  })()}
                  margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
                >
                  <defs>
                    <linearGradient id="fedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                  dataKey="date" 
                  stroke="#71717A" 
                  tick={{ fontSize: 12 }} 
                  interval="preserveStartEnd"
                />
                  <YAxis stroke="#71717A" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181B',
                      border: '1px solid #27272A',
                      borderRadius: '8px',
                      color: '#FFFFFF'
                    }}
                    labelStyle={{ color: '#A1A1AA', fontSize: '12px', marginBottom: '4px' }}
                    itemStyle={{ color: '#FFFFFF', fontSize: '14px' }}
                    formatter={(value: any) => [`${value}%`, 'Fed Funds Rate']}
                  />
                  <Area type="stepAfter" dataKey="rate" stroke="#3B82F6" fill="url(#fedGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        {yieldCurveData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Treasury Yield Curve</h3>
              <div className="text-xs text-zinc-400">
                Latest: {yieldCurveData?.data?.length > 0 ? format(parseISO(yieldCurveData.data[0].date), 'MMM d, yyyy') : 'N/A'}
              </div>
            </div>
            
            {yieldCurveData?.data && yieldCurveData.data.length > 0 && (
              <>
                {/* Latest Yield Curve */}
                <div className="h-64 mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={(() => {
                        const latest = yieldCurveData.data[0];
                        const maturities = [
                          { key: '1M', label: '1M', order: 1 },
                          { key: '2M', label: '2M', order: 2 },
                          { key: '3M', label: '3M', order: 3 },
                          { key: '4M', label: '4M', order: 4 },
                          { key: '6M', label: '6M', order: 6 },
                          { key: '1Y', label: '1Y', order: 12 },
                          { key: '2Y', label: '2Y', order: 24 },
                          { key: '3Y', label: '3Y', order: 36 },
                          { key: '5Y', label: '5Y', order: 60 },
                          { key: '7Y', label: '7Y', order: 84 },
                          { key: '10Y', label: '10Y', order: 120 },
                          { key: '20Y', label: '20Y', order: 240 },
                          { key: '30Y', label: '30Y', order: 360 }
                        ];
                        
                        return maturities
                          .filter(m => latest.rates[m.key] !== null && latest.rates[m.key] !== undefined)
                          .map(m => ({
                            maturity: m.label,
                            yield: Number(latest.rates[m.key]),
                            order: m.order
                          }))
                          .sort((a, b) => a.order - b.order);
                      })()}
                      margin={{ top: 10, right: 10, left: 10, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                      <XAxis 
                        dataKey="maturity" 
                        stroke="#71717A" 
                        tick={{ fontSize: 12 }} 
                      />
                      <YAxis stroke="#71717A" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#18181B',
                          border: '1px solid #27272A',
                          borderRadius: '8px',
                          color: '#FFFFFF'
                        }}
                        labelStyle={{ color: '#A1A1AA', fontSize: '12px', marginBottom: '4px' }}
                        itemStyle={{ color: '#FFFFFF', fontSize: '14px' }}
                        formatter={(value: any) => [`${value}%`, 'Yield']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="yield" 
                        stroke="#F59E0B" 
                        strokeWidth={3}
                        dot={{ fill: '#F59E0B', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: '#F59E0B', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Key Spreads */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {(() => {
                    const latest = yieldCurveData.data[0];
                    const rates = latest.rates;
                    const spreads = [
                      { 
                        name: '10Y-2Y Spread', 
                        value: rates['10Y'] && rates['2Y'] ? Number(rates['10Y']) - Number(rates['2Y']) : null,
                        description: 'Key recession indicator'
                      },
                      { 
                        name: '10Y-3M Spread', 
                        value: rates['10Y'] && rates['3M'] ? Number(rates['10Y']) - Number(rates['3M']) : null,
                        description: 'Traditional indicator'
                      },
                      { 
                        name: '30Y-10Y Spread', 
                        value: rates['30Y'] && rates['10Y'] ? Number(rates['30Y']) - Number(rates['10Y']) : null,
                        description: 'Long-term steepness'
                      },
                      { 
                        name: '2Y-3M Spread', 
                        value: rates['2Y'] && rates['3M'] ? Number(rates['2Y']) - Number(rates['3M']) : null,
                        description: 'Short-term steepness'
                      }
                    ];

                    return spreads.map((spread, index) => (
                      <div key={index} className="bg-zinc-800/30 rounded-lg p-3">
                        <p className="text-xs text-zinc-400 mb-1">{spread.name}</p>
                        <p className={`text-lg font-semibold ${
                          spread.value === null ? 'text-zinc-500' :
                          spread.value > 0 ? 'text-emerald-400' : 'text-rose-400'
                        }`}>
                          {spread.value === null ? 'N/A' : `${spread.value > 0 ? '+' : ''}${spread.value.toFixed(2)}%`}
                        </p>
                        <p className="text-xs text-zinc-500">{spread.description}</p>
                      </div>
                    ));
                  })()}
                </div>

                {/* Yield Curve Historical Trend */}
                <div>
                  <h4 className="text-sm font-medium text-zinc-300 mb-3">
                    10Y-2Y Spread History (Recession Indicator)
                    <span className="text-xs text-zinc-500 ml-2">
                      {(() => {
                        const data = yieldCurveData.data || [];
                        if (data.length > 0) {
                          const oldest = data[data.length - 1]?.date;
                          const newest = data[0]?.date;
                          if (oldest && newest) {
                            return `• ${format(parseISO(oldest), 'MMM dd')} to ${format(parseISO(newest), 'MMM dd')}`;
                          }
                        }
                        return '';
                      })()}
                    </span>
                  </h4>
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={(() => {
                          // Since we have limited historical data (~6 weeks), show all available data for most timeframes
                          const allData = yieldCurveData.data || [];
                          let dataToShow = allData;
                          
                          // Only filter for very short timeframes since we have limited data
                          if (selectedTimeframe === '1M') {
                            const oneMonthAgo = subMonths(new Date(), 1);
                            dataToShow = allData.filter((d: any) => isAfter(parseISO(d.date), oneMonthAgo));
                          }
                          
                          // Sort chronologically (oldest to newest) and process
                          return dataToShow
                            .filter((d: any) => d.rates['10Y'] !== null && d.rates['2Y'] !== null)
                            .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                            .map((d: any) => ({
                              date: format(parseISO(d.date), 'MMM dd'),
                              fullDate: format(parseISO(d.date), 'MMM dd, yyyy'),
                              spread: Number(d.rates['10Y']) - Number(d.rates['2Y'])
                            }));
                        })()}
                        margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
                      >
                        <defs>
                          <linearGradient id="spreadGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="spreadGradientNegative" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                            <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272A" opacity={0.3} />
                        <XAxis 
                          dataKey="date" 
                          stroke="#71717A" 
                          tick={{ fontSize: 10 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          stroke="#71717A" 
                          tick={{ fontSize: 10 }} 
                          tickFormatter={(v) => `${v}%`}
                          domain={['dataMin - 0.5', 'dataMax + 0.5']}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#18181B',
                            border: '1px solid #27272A',
                            borderRadius: '8px',
                            color: '#FFFFFF'
                          }}
                          labelStyle={{ color: '#A1A1AA', fontSize: '12px', marginBottom: '4px' }}
                          itemStyle={{ color: '#FFFFFF', fontSize: '14px' }}
                          formatter={(value: any) => [
                            <span style={{ color: value >= 0 ? '#10B981' : '#EF4444' }}>
                              {`${value >= 0 ? '+' : ''}${value.toFixed(2)}%`}
                            </span>,
                            '10Y-2Y Spread'
                          ]}
                        />
                        <ReferenceLine y={0} stroke="#71717A" strokeDasharray="3 3" />
                        <Area
                          type="monotone"
                          dataKey="spread"
                          stroke="#10B981"
                          strokeWidth={2}
                          fill="url(#spreadGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800 rounded-xl p-6 mt-6"
        >
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Info className="w-5 h-5 text-violet-400" />
            Understanding Markets Data
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-zinc-300">
            <div>
              <h5 className="font-medium text-white mb-2">Federal Funds Rate</h5>
              <p>The target interest rate set by the Federal Reserve for overnight lending between banks. Primary tool for monetary policy and economic control.</p>
            </div>
            <div>
              <h5 className="font-medium text-white mb-2">Treasury Yield Curve</h5>
              <p>Shows interest rates across different Treasury bond maturities. Shape indicates market expectations for economic growth and inflation.</p>
            </div>
            <div>
              <h5 className="font-medium text-white mb-2">Rate Changes Impact</h5>
              <p>Higher rates typically slow economic growth and reduce inflation. Lower rates stimulate borrowing, spending, and investment activity.</p>
            </div>
            <div>
              <h5 className="font-medium text-white mb-2">Data Source</h5>
              <p>Federal Reserve Economic Data (FRED) and U.S. Treasury. Updated daily for rates, with FOMC meetings setting policy direction.</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="animate-spin w-8 h-8 text-violet-400" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white text-lg mb-2">Error loading data</p>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  // Extract series data
  const series = treasuryData?.series || [];
  const debtSeries = series.filter(s => s.name.toLowerCase().includes('debt'));
  const deficitSeries = series.filter(s => 
    s.name.toLowerCase().includes('deficit') || s.name.toLowerCase().includes('surplus')
  );

  const tabs = [
    { id: 'fiscal' as TabType, name: 'Fiscal', icon: DollarSign },
    { id: 'inflation' as TabType, name: 'Inflation', icon: TrendingUp },
    { id: 'markets' as TabType, name: 'Markets', icon: LineChartIcon },
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold text-white mb-2">TradFi Dashboard</h1>
        <p className="text-zinc-400">Real-time financial market data and economic indicators</p>
      </motion.div>

      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="flex gap-2 flex-wrap">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  navigate(`/tradfi/${tab.id}`, { replace: true });
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/25'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeframe selector - available for all tabs */}
      <div className="mb-6">
        <div className="flex gap-2 flex-wrap">
          {(['1M', '3M', '6M', '1Y', '5Y', 'ALL'] as TimeframeType[]).map((period) => (
            <button
              key={period}
              onClick={() => setSelectedTimeframe(period)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedTimeframe === period
                  ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/25'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'fiscal' && (
            <>
              {/* Key Metrics Dashboard */}
              {series.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {series.map(s => renderMetricCard(s))}
                </div>
              )}

              {/* Detailed Charts */}
              <div className="space-y-6">
                {debtSeries.map(s => renderDebtChart(s))}
                {deficitSeries.map(s => renderDeficitChart(s))}
              </div>

              {/* Information Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800 rounded-xl p-6 mt-6"
              >
                <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5 text-violet-400" />
                  Understanding Fiscal Data
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-zinc-300">
                  <div>
                    <h5 className="font-medium text-white mb-2">Total Public Debt</h5>
                    <p>Total amount of money owed by the U.S. government to creditors. Includes debt held by the public and intragovernmental holdings.</p>
                  </div>
                  <div>
                    <h5 className="font-medium text-white mb-2">Budget Deficit/Surplus</h5>
                    <p>Monthly difference between government revenues and expenditures. Deficit (red) means spending exceeds revenue, surplus (green) means revenue exceeds spending.</p>
                  </div>
                  <div>
                    <h5 className="font-medium text-white mb-2">Debt Impact</h5>
                    <p>Rising debt levels can affect interest rates, inflation, and economic growth. Debt-to-GDP ratio is a key measure of fiscal sustainability.</p>
                  </div>
                  <div>
                    <h5 className="font-medium text-white mb-2">Data Source</h5>
                    <p>U.S. Treasury Fiscal Data API and Monthly Treasury Statement. Updated daily for debt, monthly for budget figures.</p>
                  </div>
                </div>
              </motion.div>
            </>
          )}

          {activeTab === 'inflation' && renderInflationTab()}
          {activeTab === 'markets' && renderMarketsTab()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default TradFiPage;